/* LOCAL PROJECT STORAGE — IndexedDB, no server required.

   This is what makes projects usable before Supabase is even connected: a
   local `.json` project already works without signing in, but it's a one-shot
   file download — it can't be renamed, duplicated or overwritten in place the
   way a saved, named list can. IndexedDB (not localStorage) because a project
   can carry embedded images/models as data URLs, easily multiple MB, well
   past localStorage's ~5-10MB-per-origin ceiling.

   One object store, keyed by a client-generated id. Every exported function is
   its own transaction — simpler and safer than trying to keep one alive
   across an await, which is the usual way hand-rolled IndexedDB code breaks. */

const DB_NAME = 'illusio-projects';
const STORE = 'projects';
const DB_VERSION = 1;

function openDB(){
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE, { keyPath: 'id' }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const asPromise = r => new Promise((resolve, reject) => {
  r.onsuccess = () => resolve(r.result);
  r.onerror = () => reject(r.error);
});

async function withStore(mode, fn){
  const db = await openDB();
  const store = db.transaction(STORE, mode).objectStore(STORE);
  return fn(store);
}

const getAllRows = () => withStore('readonly', store => asPromise(store.getAll()));
const getRow     = id => withStore('readonly', store => asPromise(store.get(id)));
const putRow     = record => withStore('readwrite', store => asPromise(store.put(record)));
const deleteRow  = id => withStore('readwrite', store => asPromise(store.delete(id)));

const notFound = () => { throw new Error('That project no longer exists — it may have been deleted in another tab.'); };

/** Newest first, same ordering `listProjects()` returns for cloud projects. */
export async function listLocal(){
  const rows = await getAllRows();
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getLocal(id){
  return getRow(id);
}

export async function saveLocal(title, doc){
  const now = Date.now();
  const record = { id: crypto.randomUUID(), title, doc, createdAt: now, updatedAt: now };
  await putRow(record);
  return record;
}

export async function overwriteLocal(id, doc){
  const existing = await getRow(id);
  if(!existing) notFound();
  const record = { ...existing, doc, updatedAt: Date.now() };
  await putRow(record);
  return record;
}

export async function renameLocal(id, title){
  const existing = await getRow(id);
  if(!existing) notFound();
  const record = { ...existing, title, updatedAt: Date.now() };
  await putRow(record);
  return record;
}

/** Same " copy" suffix convention scene/objects.js's duplicateSel() already uses. */
export async function duplicateLocal(id){
  const existing = await getRow(id);
  if(!existing) notFound();
  const now = Date.now();
  const record = { ...existing, id: crypto.randomUUID(), title: existing.title + ' copy', createdAt: now, updatedAt: now };
  await putRow(record);
  return record;
}

export async function deleteLocal(id){
  await deleteRow(id);
}
